import { createHash } from "node:crypto";

import type {
  ActionV3,
  AssetKind,
  ScriptV3,
  SpeakerV3,
} from "../v3/types.js";
import { parseAssetTarget } from "../v3/address.js";
import { sortDeep } from "../text-util.js";
import { generateV4Id, isV4Id, type V4IdByKind, type V4IdKind } from "./ids.js";
import type {
  ActionV4,
  ActorIdV4,
  ActorRefV4,
  LocationIdV4,
  PropIdV4,
  PropRefV4,
  ScriptV4,
  SpeakerV4,
  StateIdV4,
} from "./types.js";

type AssetIdMap = {
  actor: Map<string, ActorIdV4>;
  location: Map<string, LocationIdV4>;
  prop: Map<string, PropIdV4>;
};

export interface V4IdentityMap {
  version: 3;
  actor: Record<string, ActorIdV4>;
  location: Record<string, LocationIdV4>;
  prop: Record<string, PropIdV4>;
  state: Record<string, Record<string, StateIdV4>>;
  episode: Record<string, V4IdByKind["episode"]>;
  scene: Record<string, V4IdByKind["scene"]>;
  action: Record<string, V4IdByKind["action"]>;
}

export interface V4ConversionResult {
  script: ScriptV4;
  identityMap: V4IdentityMap;
}

export interface V3IdentityKeys {
  actor?: Readonly<Record<string, string>>;
  location?: Readonly<Record<string, string>>;
  prop?: Readonly<Record<string, string>>;
  state?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  episode?: Readonly<Record<string, string>>;
  scene?: Readonly<Record<string, string>>;
  action?: Readonly<Record<string, string>>;
}

export function emptyV4IdentityMap(): V4IdentityMap {
  return {
    version: 3,
    actor: {},
    location: {},
    prop: {},
    state: {},
    episode: {},
    scene: {},
    action: {},
  };
}

function requiredMapped<T extends string>(map: ReadonlyMap<string, T>, oldId: string): T {
  const value = map.get(oldId);
  if (value === undefined) throw new Error(`Missing mapped ID for ${oldId}`);
  return value;
}

export function newScriptV4FromV3WithIdentity(
  script: ScriptV3,
  prior: V4IdentityMap = emptyV4IdentityMap(),
  sourceKeys: V3IdentityKeys = {},
): V4ConversionResult {
  if (prior.version !== 3) throw new Error(`Unsupported v4 identity map version: ${String(prior.version)}`);
  const identityMap = structuredClone(prior);
  const ids: AssetIdMap = {
    actor: new Map(),
    location: new Map(),
    prop: new Map(),
  };
  const used = {
    actor: new Set<string>(),
    location: new Set<string>(),
    prop: new Set<string>(),
    episode: new Set<string>(),
    scene: new Set<string>(),
    action: new Set<string>(),
  };
  const allocate = <K extends Exclude<V4IdKind, "state">>(
    kind: K,
    key: string,
    map: Record<string, V4IdByKind[K]>,
    existing: Set<string>,
  ): V4IdByKind[K] => {
    const saved = map[key];
    if (saved !== undefined) {
      if (!isV4Id(kind, saved)) throw new Error(`Invalid persisted ${kind} ID for ${key}: ${saved}`);
      if (existing.has(saved)) throw new Error(`Duplicate persisted ${kind} ID: ${saved}`);
      existing.add(saved);
      return saved;
    }
    const generated = generateV4Id(kind, existing);
    existing.add(generated);
    map[key] = generated;
    return generated;
  };
  const assetSourceKey = (
    kind: "actor" | "location" | "prop",
    id: string,
  ): string => sourceKeys[kind]?.[id] ?? id;
  for (const actor of script.actors) {
    const id = allocate(
      "actor",
      assetSourceKey("actor", actor.actor_id),
      identityMap.actor,
      used.actor,
    );
    ids.actor.set(actor.actor_id, id);
  }
  for (const location of script.locations) {
    const id = allocate(
      "location",
      assetSourceKey("location", location.location_id),
      identityMap.location,
      used.location,
    );
    ids.location.set(location.location_id, id);
  }
  for (const prop of script.props) {
    const id = allocate(
      "prop",
      assetSourceKey("prop", prop.prop_id),
      identityMap.prop,
      used.prop,
    );
    ids.prop.set(prop.prop_id, id);
  }

  const stateMaps = new Map<string, Map<string, StateIdV4>>();
  const convertStates = (
    ownerKind: "actor" | "location" | "prop",
    ownerId: string,
    states: ScriptV3["actors"][number]["states"],
  ): ScriptV4["actors"][number]["states"] => {
    const map = new Map<string, StateIdV4>();
    const usedStates = new Set<string>();
    const ownerSourceKey = assetSourceKey(ownerKind, ownerId);
    const persisted = identityMap.state[ownerSourceKey] ?? {};
    identityMap.state[ownerSourceKey] = persisted;
    const converted = states.map((state) => {
      let stateId: StateIdV4;
      if (state.state_id === "default") {
        stateId = "default";
      } else {
        const sourceKey = sourceKeys.state?.[ownerId]?.[state.state_id] ?? state.state_id;
        const saved = persisted[sourceKey];
        if (saved !== undefined) {
          if (!isV4Id("state", saved)) throw new Error(`Invalid persisted state ID for ${ownerId}/${state.state_id}: ${saved}`);
          if (usedStates.has(saved)) throw new Error(`Duplicate persisted state ID on ${ownerId}: ${saved}`);
          stateId = saved;
        } else {
          stateId = generateV4Id("state", usedStates);
          persisted[sourceKey] = stateId;
        }
      }
      usedStates.add(stateId);
      map.set(state.state_id, stateId);
      return { ...state, state_id: stateId };
    });
    stateMaps.set(ownerId, map);
    return converted;
  };
  const stateId = (ownerId: string, oldStateId: string | undefined): StateIdV4 | undefined =>
    oldStateId === undefined ? undefined : requiredMapped(stateMaps.get(ownerId) ?? new Map(), oldStateId);

  const actors = script.actors.map((actor) => ({
    ...actor,
    actor_id: requiredMapped(ids.actor, actor.actor_id),
    states: convertStates("actor", actor.actor_id, actor.states),
  }));
  const locations = script.locations.map((location) => ({
    ...location,
    location_id: requiredMapped(ids.location, location.location_id),
    states: convertStates("location", location.location_id, location.states),
  }));
  const props = script.props.map((prop) => ({
    ...prop,
    prop_id: requiredMapped(ids.prop, prop.prop_id),
    states: convertStates("prop", prop.prop_id, prop.states),
  }));

  const speaker = (value: SpeakerV3): SpeakerV4 => {
    if ("actor_id" in value) {
      const mappedState = stateId(value.actor_id, value.state_id);
      return {
        actor_id: requiredMapped(ids.actor, value.actor_id),
        ...(mappedState === undefined ? {} : { state_id: mappedState }),
      };
    }
    return value.label === undefined
      ? { kind: value.kind }
      : { kind: value.kind, label: value.label };
  };
  const transitionTarget = (target: string): ActorIdV4 | LocationIdV4 | PropIdV4 => {
    if (target.includes(":")) {
      const [kind, oldId] = parseAssetTarget(target);
      if (kind === "actor") return requiredMapped(ids.actor, oldId);
      if (kind === "location") return requiredMapped(ids.location, oldId);
      return requiredMapped(ids.prop, oldId);
    }
    const mapped = ids.actor.get(target) ?? ids.location.get(target) ?? ids.prop.get(target);
    if (mapped === undefined) throw new Error(`Missing transition target mapping for ${target}`);
    return mapped;
  };
  const action = (value: ActionV3, key: string): ActionV4 => {
    const actionId = allocate("action", key, identityMap.action, used.action);
    const transition = value.transition_prompt === undefined
      ? {}
      : {
          transition_prompt: {
            ...value.transition_prompt,
            target: transitionTarget(value.transition_prompt.target),
          },
        };
    if (value.type === "dialogue" || value.type === "inner_thought") {
      if (value.speaker === undefined) {
        throw new Error(`Speaking action ${value.content.slice(0, 40)} has no speaker`);
      }
      return {
        action_id: actionId,
        type: value.type,
        content: value.content,
        speaker: speaker(value.speaker),
        ...(value.emotion === undefined ? {} : { parenthetical: value.emotion }),
        ...transition,
        ...(value.extend === undefined ? {} : { extend: value.extend }),
      };
    }
    return {
      action_id: actionId,
      type: value.type,
      content: value.content,
      ...transition,
      ...(value.extend === undefined ? {} : { extend: value.extend }),
    };
  };
  const refStates = (ownerId: string, oldStateId: string | undefined): StateIdV4[] =>
    [stateId(ownerId, oldStateId ?? "default") ?? "default"];
  const actorRefs = (
    refs: ScriptV3["episodes"][number]["scenes"][number]["actors"],
    actions: ScriptV3["episodes"][number]["scenes"][number]["actions"],
  ): ActorRefV4[] => {
    const merged = new Map<ActorIdV4, Set<StateIdV4>>();
    for (const ref of refs) {
      const id = requiredMapped(ids.actor, ref.actor_id);
      const states = merged.get(id) ?? new Set<StateIdV4>();
      refStates(ref.actor_id, ref.state_id).forEach((item) => states.add(item));
      merged.set(id, states);
    }
    for (const candidate of actions) {
      if (candidate.speaker === undefined || !("actor_id" in candidate.speaker)) continue;
      const oldActorId = candidate.speaker.actor_id;
      const id = requiredMapped(ids.actor, oldActorId);
      const states = merged.get(id) ?? new Set<StateIdV4>();
      refStates(oldActorId, candidate.speaker.state_id).forEach((item) => states.add(item));
      merged.set(id, states);
    }
    return [...merged].map(([actor_id, states]) => ({
      actor_id,
      state_ids: [...states].sort(),
    }));
  };
  const propRefs = (refs: ScriptV3["episodes"][number]["scenes"][number]["props"]): PropRefV4[] => {
    const merged = new Map<PropIdV4, Set<StateIdV4>>();
    for (const ref of refs) {
      const id = requiredMapped(ids.prop, ref.prop_id);
      const states = merged.get(id) ?? new Set<StateIdV4>();
      refStates(ref.prop_id, ref.state_id).forEach((item) => states.add(item));
      merged.set(id, states);
    }
    return [...merged].map(([prop_id, states]) => ({
      prop_id,
      state_ids: [...states].sort(),
    }));
  };
  const liveEpisodeKeys = new Set<string>();
  const liveSceneKeys = new Set<string>();
  const liveActionKeys = new Set<string>();

  const converted: ScriptV4 = {
    version: 4,
    title: script.title,
    worldview: script.worldview,
    style: script.style,
    ...(script.worldview_raw === undefined ? {} : { worldview_raw: script.worldview_raw }),
    ...(script.synopsis === undefined ? {} : { synopsis: script.synopsis }),
    ...(script.logline === undefined ? {} : { logline: script.logline }),
    ...(script.theme === undefined ? {} : { theme: script.theme }),
    ...(script.main_characters === undefined ? {} : { main_characters: script.main_characters }),
    ...(script.extend === undefined ? {} : { extend: script.extend }),
    actors,
    locations,
    props,
    episodes: script.episodes.map((episode) => {
      const episodeKey = sourceKeys.episode?.[episode.episode_id] ?? episode.episode_id;
      liveEpisodeKeys.add(episodeKey);
      const episodeId = allocate("episode", episodeKey, identityMap.episode, used.episode);
      return {
        ...episode,
        episode_id: episodeId,
        scenes: episode.scenes.map((scene) => {
          const sceneAddress = `${episode.episode_id}/${scene.scene_id}`;
          const sceneKey = sourceKeys.scene?.[sceneAddress] ?? sceneAddress;
          liveSceneKeys.add(sceneKey);
          const sceneId = allocate("scene", sceneKey, identityMap.scene, used.scene);
          const fallbackOccurrences = new Map<string, number>();
          return {
            ...scene,
            scene_id: sceneId,
            location: scene.location === null
              ? null
              : {
                  location_id: requiredMapped(ids.location, scene.location.location_id),
                  state_ids: refStates(scene.location.location_id, scene.location.state_id),
                },
            actors: actorRefs(scene.actors, scene.actions),
            props: propRefs(scene.props),
            actions: scene.actions.map((candidate, index) => {
              const actionAddress = `${sceneAddress}#${index}`;
              const signature = JSON.stringify(sortDeep(candidate));
              const occurrence = (fallbackOccurrences.get(signature) ?? 0) + 1;
              fallbackOccurrences.set(signature, occurrence);
              const signatureHash = createHash("sha256").update(signature).digest("hex");
              const actionKey = sourceKeys.action?.[actionAddress]
                ?? `${sceneKey}:action:${signatureHash}:${occurrence}`;
              liveActionKeys.add(actionKey);
              return action(candidate, actionKey);
            }),
          };
        }),
      };
    }),
  };
  const keepOnly = (record: Record<string, unknown>, keys: ReadonlySet<string>): void => {
    for (const key of Object.keys(record)) {
      if (!keys.has(key)) delete record[key];
    }
  };
  keepOnly(identityMap.actor, new Set(script.actors.map((asset) => assetSourceKey("actor", asset.actor_id))));
  keepOnly(identityMap.location, new Set(script.locations.map((asset) => assetSourceKey("location", asset.location_id))));
  keepOnly(identityMap.prop, new Set(script.props.map((asset) => assetSourceKey("prop", asset.prop_id))));
  keepOnly(identityMap.episode, liveEpisodeKeys);
  keepOnly(identityMap.scene, liveSceneKeys);
  keepOnly(identityMap.action, liveActionKeys);
  const owners = [
    ...script.actors.map((asset) => ({
      id: assetSourceKey("actor", asset.actor_id),
      oldId: asset.actor_id,
      states: asset.states,
    })),
    ...script.locations.map((asset) => ({
      id: assetSourceKey("location", asset.location_id),
      oldId: asset.location_id,
      states: asset.states,
    })),
    ...script.props.map((asset) => ({
      id: assetSourceKey("prop", asset.prop_id),
      oldId: asset.prop_id,
      states: asset.states,
    })),
  ];
  keepOnly(identityMap.state, new Set(owners.map((owner) => owner.id)));
  for (const owner of owners) {
    keepOnly(
      identityMap.state[owner.id] ?? {},
      new Set(owner.states.map((state) =>
        sourceKeys.state?.[owner.oldId]?.[state.state_id] ?? state.state_id)),
    );
  }
  return { script: converted, identityMap };
}

export function newScriptV4FromV3(script: ScriptV3): ScriptV4 {
  return newScriptV4FromV3WithIdentity(script).script;
}
